import type { Middleware, MiddlewareAPI } from "@reduxjs/toolkit";
import { changeLanguage } from "i18next";
import { setFilters, setLocale, setSelectedLanguage } from "slices/appStateSlice";
import { setTransaction } from "slices/transactionSlice";
import type { AllergenType, Maybe } from "types";
import { AppLanguage } from "types/mainTypes";
import { MixpanelActionEventsEnum } from "types/mixpanelTypes";
import { getAnalyticsInstance } from "utils/analytics/analyticsUtils";
import { log } from "utils/logger";

const mapLanguage = (language?: string) => {
	if (language !== "cs" && language !== "sk") {
		return {
			language: AppLanguage.SECONDARY,
			locale: "en-GB",
		};
	}
	return {
		language: AppLanguage.PRIMARY,
		locale: "cs-CZ",
	};
};

const handlePreselectedLanguageSwitch = (store: MiddlewareAPI, language: string) => {
	const mappedLanguage = mapLanguage(language);
	const analytics = getAnalyticsInstance();

	store.dispatch(setSelectedLanguage(mappedLanguage.language));
	changeLanguage(mappedLanguage.locale).catch((e) => {
		log(`Could not change locale ${e}`, "warning");
	});
	const currentLocale = store.getState().appState.locale;
	const fromLanguage = currentLocale?.startsWith("cs") ? "cs" : "en";
	const normalizedLanguage = language === "sk" ? "cs" : language;
	analytics.track(MixpanelActionEventsEnum.Tablet_Language_Changed, {
		$from_language: fromLanguage,
		$to_language: normalizedLanguage,
		$language: normalizedLanguage,
		$trigger_source: "user-preference",
	});
	store.dispatch(setLocale(mappedLanguage.locale));
};

const handlePreselectedFilters = (store: MiddlewareAPI, allergens: Maybe<AllergenType[]>) => {
	const analytics = getAnalyticsInstance();

	if (allergens && allergens.length > 0) {
		const currentAllergens = store.getState().appState.filters.allergens || [];
		// We want to combine allergens that were selected wit the ones from preferences
		const filteredAllergens = allergens.filter((a) => !currentAllergens.includes(a));
		const appliedFilters = [...currentAllergens, ...filteredAllergens];
		analytics.track(MixpanelActionEventsEnum.Tablet_Filter_Applied, {
			$filters: appliedFilters,
			$filter_count: appliedFilters.length,
			$trigger_source: "auto-user-preferences",
		});
		store.dispatch(
			setFilters({
				allergens: appliedFilters,
			}),
		);
	}
};

export const userPreferencesMiddleware: Middleware = (store) => (next) => (action) => {
	const result = next(action);

	if (setTransaction.match(action)) {
		const preferences = action.payload.order?.user?.preferences;
		if (preferences?.language) {
			handlePreselectedLanguageSwitch(store, preferences.language);
		}
		if (preferences?.allergens) {
			handlePreselectedFilters(store, preferences.allergens);
		}
	}

	return result;
};
